import PropTypes from "prop-types";
import { useEffect, useMemo } from "react";

// Hooks
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";

// Local Hooks/Contexts
import { usePayPlan } from "@PayPlans/context/PayPlans";

// Pay Plans Model's Factory
import FactoryModel from "./FactoryModel";

// Components
import BodyLayer from "@Layouts/components/Layers/BodyLayer";
import PayPlansSideBar from "@PayPlans/Layouts/PayPlansSideBar";

const getSubscription = (subscription, args) => {
	const { context = "", query = "" } = subscription || {};
	if (!context || !query) {
		return null;
	}
	return {
		context,
		query,
		variables: {
			...args,
		},
	};
};

const getGeneralSubscriptions = (subscriptions = new Map(), subscriptionsParams = null) => {
	const onClonePayPlan = subscriptions.get("onClonePayPlan");
	const onCreatePayPlan = subscriptions.get("onCreatePayPlan");
	const onDeletePayPlan = subscriptions.get("onDeletePayPlan");
	const onUpdateAnyPayPlan = subscriptions.get("onUpdateAnyPayPlan");
	const onUpdateAvailablePayPlan = subscriptions.get("onUpdateAvailablePayPlan");

	const { parameters = null, selectedId } = subscriptionsParams || {};

	if (!parameters || !parameters?.locationId) {
		return [];
	}

	const cloneSubscription = getSubscription({ context: "onClonePayPlan", query: onClonePayPlan?.query || "" }, parameters);
	const createSubscription = getSubscription({ context: "onCreatePayPlan", query: onCreatePayPlan?.query || "" }, parameters);
	const deleteSubscription = getSubscription({ context: "onDeletePayPlan", query: onDeletePayPlan?.query || "" }, parameters);
	const updateAnySubscription = getSubscription({ context: "onUpdateAnyPayPlan", query: onUpdateAnyPayPlan?.query || "" }, parameters);
	const updateSubscription = getSubscription(
		{ context: "onUpdateAvailablePayPlan", query: onUpdateAvailablePayPlan?.query || "" },
		{ ...parameters, _id: selectedId }
	);

	return [cloneSubscription, createSubscription, deleteSubscription, updateAnySubscription, updateSubscription].filter((item) => item);
};

const isSelectionInstantiable = ({ query, locationId, selectionsLength, positionId, addSubscription }) => {
	const primaryConditions = locationId && positionId;
	const secondaryConditions = query && selectionsLength && addSubscription;
	return primaryConditions && secondaryConditions;
};

export default function PayPlansContainer(props) {
	const { location, children } = props;

	const { addSubscription, clearSubscriptionContext } = useSubscriptions();
	const { positionId, position, services, selected, selectionIds } = usePayPlan();

	const currentPositionId = useMemo(() => positionId, [positionId]);

	// Subscriptions definitions
	const { subscriptions } = services;

	useEffect(() => {
		if (!location || !currentPositionId) {
			return;
		}

		// Required subscriptions variables
		const locationId = location?.locationId || "";

		// Subscriptions parameters
		const parameters = { locationId, position };

		const addSubscriptions = getGeneralSubscriptions(subscriptions, { parameters, selectedId: selected?._id });
		if (addSubscriptions.length && addSubscription) {
			addSubscription(["payPlanSubscriptions", currentPositionId, locationId], addSubscriptions);
		}

		const onUpdatePayPlan = subscriptions?.get("onUpdatePayPlan");
		const isInstantiable = isSelectionInstantiable({
			locationId,
			addSubscription,
			query: onUpdatePayPlan?.query,
			selectionsLength: selectionIds.length,
			positionId: currentPositionId,
		});

		if (isInstantiable) {
			for (const id of selectionIds) {
				addSubscription(
					["payPlanSelectedSubscription", currentPositionId, locationId, id],
					[
						{
							context: "onUpdatePayPlan",
							query: onUpdatePayPlan.query || "",
							variables: {
								locationId,
								_id: id,
							},
						},
					]
				);
			}
		}

		return () => {
			clearSubscriptionContext();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [addSubscription, clearSubscriptionContext, currentPositionId, location, subscriptions, selected]);

	return (
		<BodyLayer
			fixedContent={
				<PayPlansSideBar
					showButtons
					showBy="name"
					selectBy="_id"
					enableMatches
					hideMenuOrder
					hideMenuFilter
					hideMenuGroup
					addSubscription={addSubscription}
				/>
			}
		>
			<FactoryModel>{children}</FactoryModel>
		</BodyLayer>
	);
}
PayPlansContainer.propTypes = {
	children: PropTypes.node,
	location: PropTypes.object,
};
